// BREAK THE BANK — International workspace product. Production port of the design
// prototype (Every Document/Gaps/break_the_bank). Read-only intelligence board:
// 20 AI analysts + a Moderator surface one in-band pick (1.50–2.20) per
// international fixture. WALK AWAY = the elite accumulator; MEGA BANK = the full
// pool, expandable to the desk. All data is live from the get_btb_board() RPC —
// no mock data, no bet placement. Rail ticket variant (per handoff §5).

import { Component, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { getBrowserClient } from "../../supabase/browser.ts";
import { trackEvent } from "../../conversion/trackEvent.ts";
import "../../styles/btb.css";

// ── Types (data contract — STATE.md) ──
type RiskFlag = "high_confidence" | "neutral" | "high_risk" | "unresolved";
interface BtbBoard { board_date: string; status: string; selections_count: number; odds_min: number; odds_max: number; generated_at: string; }
interface ModeratorCall { prediction: string; market: string; confidence: number; edge: number | null; rationale: string; }
interface AnalystVerdict { agentId: string; agentName: string; prediction: string; chosenMarket: string; confidence: number; edge: number | null; rationale: string; dissent: boolean; }
interface BtbSelection {
  id?: string;
  home_team: string; away_team: string; league: string; country: string | null; kickoff_at: string;
  prediction: string; market: string; odds: number; confidence: number; edge: number | null; rationale: string;
  risk_flag: RiskFlag; consensus_prediction: string; dissent_count: number; avg_confidence: number; a20_dissent: boolean;
  moderator_value: ModeratorCall | null; moderator_safe: ModeratorCall | null; moderator_risk_note: string | null; moderator_scores: Record<string, number> | null;
  verdicts: AnalystVerdict[] | null; in_walk_away: boolean; walk_away_rank: number | null;
}
interface BtbBoardResponse { board: BtbBoard; selections: BtbSelection[]; }

// ── Icons (verbatim glyphs from the prototype) ──
function Ico({ d, size = 16 }: { d: ReactNode; size?: number }): JSX.Element {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">{d}</svg>;
}
const BIC = {
  vault: <><rect x="3" y="4" width="18" height="16" rx="2" /><circle cx="12" cy="12" r="4" /><path d="M12 9v.01M12 14.5v.01M9.5 12h.01M14.5 12h.01" /></>,
  chev: <path d="M9 6l6 6-6 6" />,
  inbox: <><path d="M22 12h-6l-2 3h-4l-2-3H2" /><path d="M5.5 5h13l3.5 7v6a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1v-6z" /></>,
};

// ── Error boundary — contains any render throw to a card instead of blanking
//    the whole app (React unmounts the tree on an uncaught render error). ──
class BtbBoundary extends Component<{ children: ReactNode }, { error: boolean }> {
  override state = { error: false };
  static getDerivedStateFromError(): { error: boolean } { return { error: true }; }
  override componentDidCatch(e: Error): void { console.error("[BreakTheBank] render error:", e); }
  override render(): ReactNode {
    if (this.state.error) {
      return (
        <div className="state-wrap">
          <div className="state-icon"><Ico d={BIC.inbox} size={26} /></div>
          <div className="state-title">Couldn&rsquo;t render the board</div>
          <div className="state-body">Something went wrong loading this view. Refresh the page to try again — your board is safe.</div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ── Helpers (kept from prototype/data.jsx) ──
const BTB_MARKETS: Record<string, string> = {
  total_goals_under_5_5: "Under 5.5 Goals", total_goals_under_4_5: "Under 4.5 Goals", total_goals_under_3_5: "Under 3.5 Goals",
  total_goals_under_2_5: "Under 2.5 Goals", total_goals_under_1_5: "Under 1.5 Goals", total_goals_over_1_5: "Over 1.5 Goals",
  total_goals_over_2_5: "Over 2.5 Goals", total_goals_over_3_5: "Over 3.5 Goals", btts_no: "Both Teams To Score — No",
  btts_yes: "Both Teams To Score — Yes", double_chance_1x: "Double Chance (Home / Draw)", double_chance_x2: "Double Chance (Draw / Away)",
  dnb_home: "Draw No Bet — Home", dnb_away: "Draw No Bet — Away", home_win: "Home Win", away_win: "Away Win",
  handicap_home_minus_1_5: "Home −1.5", handicap_away_minus_1_5: "Away −1.5", handicap_home_plus_0_5: "Home +0.5",
  "match_winner:home": "Home Win", "match_winner:draw": "Draw", "match_winner:away": "Away Win",
};
const mkt = (c: string): string => BTB_MARKETS[c] || c;
function confLabel(c: number): { label: string; cls: string } {
  if (c >= 85) return { label: "Very High", cls: "conf-very" };
  if (c >= 70) return { label: "Strong", cls: "conf-strong" };
  if (c >= 55) return { label: "Moderate", cls: "conf-mod" };
  return { label: "Low", cls: "conf-low" };
}
const fmtOdds = (o: number): string => (typeof o === "number" && Number.isFinite(o) ? o : 0).toFixed(2);
// edge can be null/absent in live data (a model/verdict that didn't carry one) — never throw.
const fmtEdge = (e: number | null | undefined): string =>
  (e == null || !Number.isFinite(e)) ? "—" : (e > 0 ? "+" : "") + e.toFixed(1) + "%";
const edgeCls = (e: number | null | undefined): string => (e != null && e >= 0 ? "pos" : "neg");
const kickoffTime = (iso: string): string =>
  new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: "UTC" }) + " GMT";

// ── Count-up (from prototype/hooks.jsx) ──
function useCountUp(target: number, opts: { duration?: number } = {}): number {
  const { duration } = opts;
  const [val, setVal] = useState(target);
  const fromRef = useRef(target);
  const rafRef = useRef<number | null>(null);
  useEffect(() => {
    const from = fromRef.current, to = target;
    if (from === to) { setVal(to); return; }
    const mag = Math.abs(to);
    const dur = duration != null ? duration : mag >= 1e6 ? 1100 : mag >= 1e5 ? 850 : mag >= 1e3 ? 600 : 460;
    const t0 = performance.now();
    const ease = (p: number) => 1 - Math.pow(1 - p, 3);
    const tick = (now: number) => {
      const p = Math.min(1, (now - t0) / dur);
      setVal(from + (to - from) * ease(p));
      if (p < 1) rafRef.current = requestAnimationFrame(tick);
      else { fromRef.current = to; setVal(to); }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current != null) cancelAnimationFrame(rafRef.current); };
  }, [target, duration]);
  return val;
}

// ── Data hook — the single source ──
function useBtbBoard(): { loading: boolean; data: BtbBoardResponse | null } {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<BtbBoardResponse | null>(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const client = getBrowserClient() as unknown as { rpc: (fn: string) => Promise<{ data: unknown }> };
        const res = await client.rpc("get_btb_board");
        if (!alive) return;
        const d = (res?.data ?? null) as BtbBoardResponse | null;
        setData(d && d.board ? d : null);
        if (d?.board) trackEvent("btb_board_viewed", { board_date: d.board.board_date, selections: Array.isArray(d.selections) ? d.selections.length : 0 });
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);
  return { loading, data };
}

// ── Masthead ──
function Masthead({ board }: { board: BtbBoard }): JSX.Element {
  const date = new Date(board.board_date + "T00:00:00Z").toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", timeZone: "UTC" });
  const asm = board.generated_at ? new Date(board.generated_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: "UTC" }) : null;
  return (
    <div className="masthead">
      <div>
        <div className="mh-eyebrow">International · The Desk</div>
        <h1 className="mh-title">Break The Bank</h1>
        <p className="mh-sub">Twenty AI analysts and a Moderator read every international fixture and
          surface the single best in-band pick on each. One shared board, assembled fresh every match-day.</p>
      </div>
      <div className="mh-meta">
        <div className="mh-date">Match-day · <b>{date}</b></div>
        <div className="mh-status"><span className="pulse"></span>Fresh · assembled {asm ?? "06:00"} GMT</div>
        <div className="mh-pills">
          <div className="mh-pill"><span className="k">Band</span><span className="v amber">{board.odds_min.toFixed(2)}–{board.odds_max.toFixed(2)}</span></div>
          <div className="mh-pill"><span className="k">Selections</span><span className="v">{board.selections_count}</span></div>
        </div>
      </div>
    </div>
  );
}

// ── Tier switch ──
function TierSwitch({ tier, setTier, waCount, mbCount }: { tier: "walkaway" | "megabank"; setTier: (t: "walkaway" | "megabank") => void; waCount: number; mbCount: number }): JSX.Element {
  const opts: Array<{ id: "walkaway" | "megabank"; label: string; cnt: number }> = [
    { id: "walkaway", label: "Walk Away", cnt: waCount },
    { id: "megabank", label: "Mega Bank", cnt: mbCount },
  ];
  const idx = opts.findIndex((o) => o.id === tier);
  return (
    <div className="tierbar">
      <div className="seg" role="tablist">
        <div className="seg-thumb" style={{ left: `calc(4px + ${idx} * (50% - 4px))`, width: "calc(50% - 4px)" }}></div>
        {opts.map((o) => (
          <button key={o.id} className={tier === o.id ? "on" : ""} role="tab" aria-selected={tier === o.id} onClick={() => setTier(o.id)}>
            {o.label}<span className="cnt">{o.cnt}</span>
          </button>
        ))}
      </div>
      <div className="tier-caption">
        {tier === "walkaway"
          ? <>The elite ten, assembled as <b>one accumulator</b>.</>
          : <>The full pool — <b>every qualifying pick</b>, expandable to the desk.</>}
      </div>
    </div>
  );
}

// ── States ──
function LoadingState(): JSX.Element {
  return (
    <div>
      <div className="tierbar"><div className="sk" style={{ width: 240, height: 46, borderRadius: 8 }}></div></div>
      <div className="wa-grid">
        <div className="panel">
          <div className="sk" style={{ height: 50, margin: 0, borderRadius: 0 }}></div>
          {Array.from({ length: 8 }).map((_, i) => <div key={i} className="sk sk-row" style={{ margin: "8px 16px" }}></div>)}
        </div>
        <div className="sk" style={{ height: 420, borderRadius: 12 }}></div>
      </div>
    </div>
  );
}
function EmptyState(): JSX.Element {
  return (
    <div className="state-wrap surface-enter">
      <div className="state-icon"><Ico d={BIC.inbox} size={26} /></div>
      <div className="state-title">No board yet</div>
      <div className="state-body">A fresh one is assembled each match-day. Check back after the morning generation completes.</div>
    </div>
  );
}

// ── WALK AWAY ──
function LegRow({ s }: { s: BtbSelection }): JSX.Element {
  const cl = confLabel(s.confidence);
  return (
    <div className="leg">
      <div className="leg-rank">{String(s.walk_away_rank ?? 0).padStart(2, "0")}</div>
      <div className="leg-main">
        <div className="leg-fix">{s.home_team}<span className="vs">v</span>{s.away_team}</div>
        <div className="leg-sub">
          <span>{s.league}</span><span className="d"></span><span className="mono">{kickoffTime(s.kickoff_at)}</span>
        </div>
        <div className="leg-pick">
          <span className="pred">{s.prediction}</span>
          <span className="mkt">{s.market}</span>
        </div>
      </div>
      <div className="leg-odds">
        <div className="o">{fmtOdds(s.odds)}</div>
        <div className={`c mono ${cl.cls}`}>{cl.label}</div>
      </div>
    </div>
  );
}

function Ticket({ legs, totalOdds, stake, setStake }: { legs: BtbSelection[]; totalOdds: number; stake: number; setStake: (n: number) => void }): JSX.Element {
  const winnings = useCountUp(stake * totalOdds);
  const odds = useCountUp(totalOdds, { duration: undefined });
  const chips = [10, 25, 50, 100];
  const longest = legs.length ? Math.max(...legs.map((l) => l.odds)) : 0;
  const shortest = legs.length ? Math.min(...legs.map((l) => l.odds)) : 0;
  const avgConf = legs.length ? Math.round(legs.reduce((a, l) => a + l.confidence, 0) / legs.length) : 0;
  return (
    <div className="ticket">
      <div className="ticket-head">
        <span className="tk">Walk Away</span>
        <span className="tn">{legs.length}-leg accumulator</span>
      </div>
      <div className="ticket-body">
        <div className="tk-block">
          <div className="tk-lbl">Combined total odds</div>
          <div className="tk-odds"><span className="x">×</span>{odds.toFixed(2)}</div>
        </div>
        <div className="tk-block">
          <div className="tk-lbl">Your stake</div>
          <div className="tk-input-wrap">
            <span className="pfx">$</span>
            <input className="tk-input mono" type="number" min="0" inputMode="decimal"
              value={stake} onChange={(e) => setStake(Math.max(0, Number(e.target.value) || 0))} />
          </div>
          <div className="tk-chips">
            {chips.map((c) => <button key={c} className={`tk-chip ${stake === c ? "on" : ""}`} onClick={() => setStake(c)}>${c}</button>)}
          </div>
        </div>
      </div>
      <div className="tk-win">
        <div className="tk-lbl">Potential winnings</div>
        <div className="tk-win-val"><span className="cur">$</span>{Math.round(winnings).toLocaleString("en-US")}</div>
      </div>
      <div className="perf"></div>
      <div className="tk-meta">
        <div className="m"><div className="k">Legs</div><div className="v">{legs.length}</div></div>
        <div className="m"><div className="k">Avg confidence</div><div className="v">{avgConf}</div></div>
        <div className="m"><div className="k">Longest leg</div><div className="v">{fmtOdds(longest)}</div></div>
        <div className="m"><div className="k">Shortest leg</div><div className="v">{fmtOdds(shortest)}</div></div>
      </div>
      <div className="tk-risk">
        <span className="tag">Note</span>
        <span>Every leg sits inside the 1.50–2.20 band. One result settles the whole accumulator — the desk surfaces the picks; the call is yours.</span>
      </div>
      <div className="tk-foot">Read-only intelligence · no bet placement</div>
    </div>
  );
}

function WalkAway({ selections, stake, setStake }: { selections: BtbSelection[]; stake: number; setStake: (n: number) => void }): JSX.Element {
  const legs = useMemo(() => selections.filter((s) => s.in_walk_away).sort((a, b) => (a.walk_away_rank ?? 99) - (b.walk_away_rank ?? 99)), [selections]);
  const totalOdds = useMemo(() => legs.reduce((p, l) => p * l.odds, 1), [legs]);
  return (
    <div className="wa-grid surface-enter">
      <div className="panel">
        <div className="panel-head">
          <span className="pk">Walk Away</span>
          <span className="pt">The elite {legs.length}</span>
          <span className="pr">Ranked by conviction × value</span>
        </div>
        {legs.map((s) => <LegRow key={s.walk_away_rank} s={s} />)}
      </div>
      <Ticket legs={legs} totalOdds={totalOdds} stake={stake} setStake={setStake} />
    </div>
  );
}

// ── MEGA BANK ──
function ScoreRing({ score }: { score: number }): JSX.Element {
  const c = score >= 75 ? "var(--success-text)" : score >= 55 ? "var(--amber)" : score >= 40 ? "var(--warning-text)" : "var(--danger-text)";
  return (
    <div className="mscore" style={{ ["--ring-c" as string]: c, ["--ring-deg" as string]: (score / 100 * 360) + "deg" } as React.CSSProperties} title={`Moderator score ${score}`}>
      <span className="num">{score}</span>
    </div>
  );
}
function AgentCard({ v, score, i }: { v: AnalystVerdict; score: number; i: number }): JSX.Element {
  const a20 = v.agentId === "A20";
  return (
    <div className={`agent ${a20 ? "a20" : v.dissent ? "dissent" : ""}`} style={{ animationDelay: i * 28 + "ms" }}>
      <div className="agent-top">
        <span className="agent-id">{v.agentId}</span>
        <div className="agent-name">{v.agentName}
          <span className="role">{a20 ? "Devil's Advocate" : v.dissent ? "Dissent" : "Analyst"}</span>
        </div>
        <ScoreRing score={score} />
      </div>
      {(v.dissent || a20) && <span className="agent-tag">{a20 ? "Risk" : "Off-consensus"}</span>}
      <div className="agent-pick">{v.prediction}<span className="mk">{v.chosenMarket}</span></div>
      <div className="agent-bars">
        <div className="agent-conf">
          <div className="ct"><div className="cf" style={{ width: v.confidence + "%" }}></div></div>
          <div className="cv">CONF {v.confidence}</div>
        </div>
        <div className={`agent-edge ${edgeCls(v.edge)}`}>{fmtEdge(v.edge)}</div>
      </div>
      <div className="agent-rat">{v.rationale}</div>
    </div>
  );
}
function Swarm({ s }: { s: BtbSelection }): JSX.Element {
  const verdicts = s.verdicts ?? [];
  const agree = 19 - s.dissent_count;
  const scores = s.moderator_scores ?? {};
  return (
    <>
      <div className="det-section-lbl">The Panel — 20 Analysts
        <span className="meta">A20 is the Devil&rsquo;s Advocate · dissent flagged</span>
      </div>
      <div className="swarm-consensus">
        <div className="ctxt">Consensus <b>{mkt(s.consensus_prediction)}</b></div>
        <div className="agreebar">
          <div className="track"><div className="fill" style={{ width: (agree / 19 * 100) + "%" }}></div></div>
          <span className="lab">{agree} of 19 agree · {s.dissent_count} dissent</span>
        </div>
      </div>
      <div className="swarm">
        {verdicts.map((v, i) => <AgentCard key={v.agentId} v={v} score={scores[v.agentId] ?? 0} i={i} />)}
      </div>
    </>
  );
}
function ModeratorBlock({ s }: { s: BtbSelection }): JSX.Element {
  const mv = s.moderator_value, ms = s.moderator_safe;
  return (
    <>
      <div className="det-section-lbl">The Moderator
        <span className="meta">21st agent · scores the panel, names the calls</span>
      </div>
      <div className="mod-grid">
        {mv && (
          <div className="mod-card value">
            <div className="mc-k">Best Value</div>
            <div className="mc-pred">{mv.prediction}</div>
            <div className="mc-mkt">{mv.market}</div>
            <div className="mc-stats">
              <div className="s"><div className="k">Conf.</div><div className="v">{mv.confidence}</div></div>
              <div className="s"><div className="k">Edge</div><div className="v" style={{ color: "var(--success-text)" }}>{fmtEdge(mv.edge)}</div></div>
            </div>
            <div className="mc-rat">{mv.rationale}</div>
          </div>
        )}
        {ms && (
          <div className="mod-card safe">
            <div className="mc-k">Most Likely</div>
            <div className="mc-pred">{ms.prediction}</div>
            <div className="mc-mkt">{ms.market}</div>
            <div className="mc-stats">
              <div className="s"><div className="k">Conf.</div><div className="v">{ms.confidence}</div></div>
              <div className="s"><div className="k">Edge</div><div className="v" style={{ color: "var(--success-text)" }}>{fmtEdge(ms.edge)}</div></div>
            </div>
            <div className="mc-rat">{ms.rationale}</div>
          </div>
        )}
        <div className="mod-card risk">
          <div className="mc-k">Risk Note</div>
          <div className="mc-rat" style={{ marginTop: 2 }}>{s.moderator_risk_note}</div>
        </div>
      </div>
    </>
  );
}
const RISK_LABEL: Record<RiskFlag, string> = { high_confidence: "High Confidence", neutral: "Neutral", high_risk: "High Risk", unresolved: "Unresolved" };
function MegaRow({ s, open, onToggle }: { s: BtbSelection; open: boolean; onToggle: () => void }): JSX.Element {
  const cl = confLabel(s.confidence);
  const rank = s.in_walk_away ? String(s.walk_away_rank ?? 0).padStart(2, "0") : "—";
  return (
    <div className={`mb-card ${open ? "open" : ""}`}>
      <div className="mb-row" onClick={onToggle}>
        <div className={`mb-rank ${s.in_walk_away ? "" : "none"}`}>
          {s.in_walk_away && <Ico d={BIC.vault} size={13} />}{rank}
        </div>
        <div className="mb-fix">
          <div className="t">{s.home_team}<span className="vs">v</span>{s.away_team}</div>
          <div className="s"><span>{s.league}</span><span className="d"></span><span className="mono">{kickoffTime(s.kickoff_at)}</span></div>
        </div>
        <div className="mb-pick">
          <div className="p">{s.prediction}</div>
          <div className="m">{s.market}</div>
        </div>
        <div className="mb-odds">{fmtOdds(s.odds)}</div>
        <div className="mb-conf">
          <span className={`v ${cl.cls}`}>{s.confidence}</span>
          <span className={`l ${cl.cls}`}>{cl.label}</span>
        </div>
        <div className={`mb-edge ${edgeCls(s.edge)}`}>{fmtEdge(s.edge)}</div>
        <div className="mb-desk">
          <span className={`flag ${s.risk_flag}`}><span className="fd"></span>{RISK_LABEL[s.risk_flag]}</span>
        </div>
        <div className="mb-chev"><Ico d={BIC.chev} size={16} /></div>
      </div>
      {open && (
        <div className="mb-detail">
          <div className="detail-inner">
            <ModeratorBlock s={s} />
            <div className="desk-strip">
              <div className="desk-cell"><div className="k">Consensus</div><div className="v" style={{ fontSize: 13 }}>{mkt(s.consensus_prediction)}</div></div>
              <div className="desk-cell"><div className="k">Dissent count</div><div className="v">{s.dissent_count}<span style={{ color: "var(--text-muted)", fontWeight: 400 }}> / 19</span></div></div>
              <div className="desk-cell"><div className="k">Avg confidence</div><div className="v amber">{Math.round(s.avg_confidence)}</div></div>
              <div className="desk-cell"><div className="k">Devil&rsquo;s Advocate</div><div className={`v ${s.a20_dissent ? "warn" : ""}`}>{s.a20_dissent ? "Flagged danger" : "No objection"}</div></div>
            </div>
            <Swarm s={s} />
          </div>
        </div>
      )}
    </div>
  );
}
function MegaBank({ selections }: { selections: BtbSelection[] }): JSX.Element {
  const keyOf = (s: BtbSelection) => s.id ?? s.home_team + s.away_team;
  const [openId, setOpenId] = useState<string | null>(selections[0] ? keyOf(selections[0]) : null);
  return (
    <div className="surface-enter">
      <div className="mb-head">
        <div>Rank</div><div>Fixture</div><div>Bank pick</div>
        <div className="r">Odds</div><div>Conf.</div><div className="r">Edge</div><div>Desk</div><div></div>
      </div>
      <div className="mb-list">
        {selections.map((s) => {
          const id = keyOf(s);
          return <MegaRow key={id} s={s} open={openId === id} onToggle={() => {
            const willOpen = openId !== id;
            setOpenId(willOpen ? id : null);
            if (willOpen) trackEvent("btb_selection_expanded", { selection_id: id });
          }} />;
        })}
      </div>
    </div>
  );
}

// ── Page ──
export function BreakTheBankPage(): JSX.Element {
  const { loading, data } = useBtbBoard();
  const [tier, setTier] = useState<"walkaway" | "megabank">("walkaway");
  const [stake, setStake] = useState(25);

  const board = data?.board ?? null;
  const selections = data?.selections ?? [];
  const waCount = selections.filter((s) => s.in_walk_away).length;
  const stale = !!board && board.board_date < new Date().toISOString().slice(0, 10);

  let content: ReactNode;
  if (loading) content = <LoadingState />;
  else if (!board) content = <EmptyState />;
  else content = (
    <>
      {stale && (
        <div className="stale-banner surface-enter">
          <span className="wtag">Stale</span>
          <span>This board predates today — a fresh one is assembled each morning. Treat these picks as yesterday&rsquo;s read.</span>
        </div>
      )}
      <TierSwitch tier={tier} setTier={setTier} waCount={waCount} mbCount={selections.length} />
      {tier === "walkaway"
        ? <WalkAway selections={selections} stake={stake} setStake={setStake} />
        : <MegaBank selections={selections} />}
    </>
  );

  return (
    <div className="btb-page">
      <BtbBoundary>
        {board && <Masthead board={board} />}
        {content}
      </BtbBoundary>
    </div>
  );
}
